import axios from "axios";

// The server session can expire while a tab stays open. The SPA only checks it on a full page load
// (the router guard caches the user), so until a reload every API call failed and pages sat there
// empty. Instead: on the first 401 while signed in, sign in again and come back to the same page.
// Usually a silent bounce through Microsoft, whose own sign-in outlives ours.

// endpoints that answer 401 as a normal "not signed in", e.g. while the router guard checks
const PUBLIC_API = new Set(["/api/user", "/api/login/flash"]);
// a bounce that comes straight back expired would loop: within this window, stop at the login page
const BOUNCE_KEY = "playbook.reauthAt";
const BOUNCE_WINDOW_MS = 2 * 60 * 1000;

let redirecting = false;

function isOwnApi(url) {
  const { origin, pathname } = new URL(url, window.location.href);
  return (
    origin === window.location.origin &&
    pathname.startsWith("/api/") &&
    !PUBLIC_API.has(pathname)
  );
}

function signInAgain() {
  if (redirecting) return; // a page fires several requests; one redirect is enough
  redirecting = true;

  const next = encodeURIComponent(window.location.pathname + window.location.search);
  let lastBounce = 0;
  try {
    lastBounce = Number(sessionStorage.getItem(BOUNCE_KEY)) || 0;
    sessionStorage.setItem(BOUNCE_KEY, String(Date.now()));
  } catch {
    // storage blocked: the login page is always a safe landing
    lastBounce = Date.now();
  }
  window.location.assign(
    Date.now() - lastBounce < BOUNCE_WINDOW_MS
      ? `/?next=${next}`
      : `/auth_redirect?next=${next}`
  );
}

export function watchForExpiredSession(auth) {
  const expired = (status, url) =>
    status === 401 && auth.user && isOwnApi(url);

  const nativeFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const response = await nativeFetch(input, init);
    if (expired(response.status, response.url || String(input?.url ?? input))) {
      signInAgain();
    }
    return response;
  };

  axios.interceptors.response.use(undefined, (error) => {
    if (expired(error.response?.status, error.config?.url ?? "")) {
      signInAgain();
    }
    return Promise.reject(error);
  });
}
